/**
 * Queue operations — pure functions that return new Queues.
 *
 * See specs/001-mvp-foundation/data-model.md → State transitions for the
 * authoritative rules. The key invariants:
 *
 *   - items.length === 0  ⇒  currentIndex === -1
 *   - items.length > 0    ⇒  currentIndex ∈ [0, items.length - 1]
 *
 * The queue does NOT carry playback position — that lives only on the
 * <video> element.
 */

import type { Item, Queue } from "../../types.ts";

export function emptyQueue(name = "My Channel"): Queue {
  return { name, items: [], currentIndex: -1 };
}

function clamp(q: Queue): Queue {
  if (q.items.length === 0) return { ...q, currentIndex: -1 };
  const max = q.items.length - 1;
  if (q.currentIndex < 0) return { ...q, currentIndex: 0 };
  if (q.currentIndex > max) return { ...q, currentIndex: max };
  return q;
}

export function addItem(q: Queue, item: Item): Queue {
  const items = [...q.items, item];
  const currentIndex = q.items.length === 0 ? 0 : q.currentIndex;
  return clamp({ ...q, items, currentIndex });
}

export function removeAt(q: Queue, index: number): Queue {
  if (index < 0 || index >= q.items.length) return q;
  const items = q.items.filter((_, i) => i !== index);
  let currentIndex = q.currentIndex;
  if (index < q.currentIndex) currentIndex -= 1;
  // If we removed the current item, stay at the same index — which now
  // points at what was the next item. clamp() handles off-the-end.
  return clamp({ ...q, items, currentIndex });
}

export function reorder(q: Queue, fromIndex: number, toIndex: number): Queue {
  if (fromIndex === toIndex) return q;
  if (fromIndex < 0 || fromIndex >= q.items.length) return q;
  if (toIndex < 0 || toIndex >= q.items.length) return q;
  const items = [...q.items];
  const [moved] = items.splice(fromIndex, 1);
  items.splice(toIndex, 0, moved);

  let currentIndex = q.currentIndex;
  if (q.currentIndex === fromIndex) {
    currentIndex = toIndex;
  } else if (fromIndex < q.currentIndex && toIndex >= q.currentIndex) {
    currentIndex -= 1;
  } else if (fromIndex > q.currentIndex && toIndex <= q.currentIndex) {
    currentIndex += 1;
  }
  return clamp({ ...q, items, currentIndex });
}

export function jumpTo(q: Queue, index: number): Queue {
  if (index < 0 || index >= q.items.length) return q;
  return { ...q, currentIndex: index };
}

export function next(q: Queue): Queue {
  if (q.items.length === 0) return q;
  if (q.currentIndex + 1 < q.items.length) {
    return { ...q, currentIndex: q.currentIndex + 1 };
  }
  return q;
}

export function prev(q: Queue): Queue {
  if (q.items.length === 0) return q;
  if (q.currentIndex > 0) {
    return { ...q, currentIndex: q.currentIndex - 1 };
  }
  return q;
}

export function clear(q: Queue): Queue {
  return { ...q, items: [], currentIndex: -1 };
}

export function currentItem(q: Queue): Item | null {
  if (q.currentIndex < 0 || q.currentIndex >= q.items.length) return null;
  return q.items[q.currentIndex];
}
